import React, { useMemo, useState } from "react";

import { LayerControlGroup } from "../terrain3d/LayerControls";
import { LAYER_ORDER, LAYER_SPECS, drapedLayers, layerLabel } from "../terrain3d/layerSpecs";
import LayerBrowser from "./LayerBrowser";
import { buildSearchEntries, groupByTheme, searchEntries } from "./layerSearch";

/**
 * COMPOSER DE VUE — fabrication d'une vue pour une étape.
 *
 * Une « vue », c'est ce qui est à l'écran quand l'animateur projette : les
 * couches allumées, ce que chacune montre, et comment elle est rendue. Le
 * cadrage, lui, se capture depuis la carte : on ne saisit pas une caméra dans
 * un formulaire, on vole jusqu'à la bonne vue.
 *
 * TROIS FAÇONS D'ARRIVER À UNE DONNÉE, parce qu'un auteur n'aborde pas un
 * catalogue de cinquante indicateurs de la même manière selon qu'il sait ou
 * non ce qu'il cherche :
 *
 *   - CHERCHER quand on a le mot en tête — la recherche tolère les fautes et
 *     porte sur des paires (couche, indicateur), pas sur des couches ;
 *   - PARCOURIR quand on ne l'a pas : l'explorateur ouvre tout le catalogue,
 *     rangé par thème ;
 *   - PIOCHER dans les suggestions thématiques, juste en dessous, pour les
 *     ajouts courants.
 *
 * LE RANGEMENT EST THÉMATIQUE. Les couches restent découpées par fichier —
 * c'est une contrainte de rendu — mais elles ne sont plus PRÉSENTÉES ainsi :
 * « communes » mêlait vulnérabilité des nappes, surface irriguée et arrêtés
 * sécheresse, qui n'ont rien à faire ensemble. Les thèmes viennent du
 * manifeste (build_manifest.py), pas d'ici.
 *
 * ET UNE COUCHE ACTIVE S'INTITULE DE CE QU'ELLE MONTRE, pas de son fichier :
 * « Jours de restriction par an », avec « Communes » en provenance. C'est ce
 * que l'auteur vient de choisir ; le fichier ne l'intéresse pas.
 */

export default function ViewComposer({
  availableLayers = [],
  layerCatalog = [],
  themes = [],
  activeLayers = [],
  layerSettings = {},
  catalog = {},
  // UN SEUL point de modification, qui reçoit un patch.
  //
  // Il y en avait deux (`onChangeActiveLayers` et `onChangeLayerSettings`), et
  // c'était un bug : l'appelant construit le nouvel objet étape à partir de
  // celui du rendu courant, donc deux appels dans le même gestionnaire
  // partaient tous deux de l'état PÉRIMÉ et le second écrasait le premier.
  // Concrètement, ajouter un indicateur posait bien le réglage mais perdait
  // l'allumage de la couche — l'entrée semblait ne rien faire.
  onChange,
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  // null = fermé, "all" = tout le catalogue, un layerId = restreint à ce que
  // CETTE couche sait montrer. Le même explorateur sert donc à ajouter et à
  // changer d'indicateur — un seul rangement thématique à apprendre.
  const [browserScope, setBrowserScope] = useState(null);

  const entries = useMemo(
    () => buildSearchEntries(layerCatalog, availableLayers),
    [layerCatalog, availableLayers]
  );

  const results = useMemo(
    () => (query.trim() ? searchEntries(entries, query, { limit: 12 }) : []),
    [entries, query]
  );

  const docsFor = (layerId) => (layerCatalog || []).find((l) => l.id === layerId);

  /**
   * CE QUE MONTRE une couche, en toutes lettres.
   *
   * La carte s'intitulait du nom du FICHIER — « Communes » — et reléguait
   * l'indicateur dans une liste déroulante de vingt-neuf entrées, rangée par
   * groupe d'origine. L'auteur venait pourtant de choisir « Jours de
   * restriction par an » : c'est ça, sa couche. Le fichier n'est plus qu'une
   * provenance, affichée en second.
   */
  const shownIndicator = (layerId) => {
    const controlId = LAYER_SPECS[layerId]?.indicatorControl;
    if (!controlId) return null;
    const indicatorId = layerSettings[layerId]?.[controlId];
    const known = entries.find(
      (entry) => entry.kind === "indicator" && entry.layerId === layerId
        && (indicatorId ? entry.indicatorId === indicatorId : false)
    );
    // Aucun choix explicite : la couche affiche l'indicateur par défaut du
    // datapack, qu'on ne peut pas nommer d'ici — on le dit plutôt que
    // d'afficher un titre faux.
    return known || (indicatorId ? null : { label: null, pending: true });
  };

  // Ordre du registre plutot que l'ordre d'ajout : la pile se lit toujours de
  // la meme facon d'une etape a l'autre, ce qui compte quand on compare deux
  // vues voisines.
  const orderedActive = LAYER_ORDER.filter((id) => activeLayers.includes(id));

  const setSetting = (layerId, controlId, value) => {
    onChange({
      layerSettings: {
        ...layerSettings,
        [layerId]: { ...(layerSettings[layerId] || {}), [controlId]: value },
      },
    });
  };

  const addLayer = (layerId) => {
    if (activeLayers.includes(layerId)) return;
    onChange({ activeLayers: [...activeLayers, layerId] });
    setExpanded((prev) => ({ ...prev, [layerId]: true }));
  };

  const removeLayer = (layerId) => {
    onChange({ activeLayers: activeLayers.filter((id) => id !== layerId) });
  };

  /**
   * Un resultat de recherche fait DEUX choses : allumer la couche et poser
   * l'indicateur. C'est tout l'interet d'indexer des paires — sinon l'auteur
   * devrait cocher la couche, puis retrouver l'indicateur dans une liste de
   * vingt-neuf entrees.
   */
  const applyResult = (entry) => {
    const patch = {
      activeLayers: activeLayers.includes(entry.layerId)
        ? activeLayers
        : [...activeLayers, entry.layerId],
    };

    if (entry.kind === "indicator") {
      const controlId = LAYER_SPECS[entry.layerId]?.indicatorControl;
      if (controlId) {
        patch.layerSettings = {
          ...layerSettings,
          [entry.layerId]: { ...(layerSettings[entry.layerId] || {}), [controlId]: entry.indicatorId },
        };
      }
    }

    // Allumage et réglage partent ensemble : c'est ce qui manquait.
    onChange(patch);
    setExpanded((prev) => ({ ...prev, [entry.layerId]: true }));
    setQuery("");
  };

  // Les nappes drapees occupent toutes la meme surface : empilees, elles se
  // masquent. Le dire au moment ou l'auteur compose vaut mieux que le laisser
  // decouvrir qu'une couche "ne s'affiche pas".
  const stacked = drapedLayers(orderedActive);

  // Suggestions d'ajout, rangées par thème et limitées : la liste complète est
  // dans l'explorateur. En montrer cinquante ici ne serait pas « tout
  // proposer », ce serait rendre le panneau illisible.
  const suggestionsByTheme = groupByTheme(
    entries.filter((entry) => entry.kind === "layer" && !activeLayers.includes(entry.layerId)),
    themes
  );

  return (
    <div className="view-composer">
      <div className="view-composer-search">
        <span className="view-composer-search-icon" aria-hidden="true">⌕</span>
        <input
          className="view-composer-input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Chercher : sécheresse, nappe, irrigation, étiage…"
          aria-label="Chercher une couche ou un indicateur"
        />
        {query.trim() && (
          <div className="view-composer-results">
            {results.length === 0 && (
              <div className="builder-empty">Rien ne correspond. Essayez l'explorateur.</div>
            )}
            {results.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className="view-composer-result"
                onClick={() => applyResult(entry)}
              >
                <span className={`view-composer-result-kind is-${entry.kind}`}>
                  {entry.kind === "layer" ? "Couche" : "Indicateur"}
                </span>
                <span className="view-composer-result-label">{entry.label}</span>
                {entry.detail && <span className="view-composer-result-detail">{entry.detail}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <button type="button" className="view-composer-browse" onClick={() => setBrowserScope("all")}>
        Parcourir les {entries.length} couches et indicateurs
      </button>

      {stacked.length > 1 && (
        <p className="view-composer-warning">
          {stacked.map(layerLabel).join(", ")} sont trois nappes plaquées sur le même relief : la
          dernière recouvre les précédentes. Mieux vaut n'en garder qu'une, ou baisser l'opacité de
          celle du dessus.
        </p>
      )}

      <div className="view-composer-stack">
        {orderedActive.length === 0 && (
          <div className="builder-empty">
            Aucune couche dans cette vue. Cherchez ci-dessus, ou ajoutez-en une dans la liste.
          </div>
        )}

        {orderedActive.map((layerId) => {
          const spec = LAYER_SPECS[layerId];
          const docs = docsFor(layerId);
          const isOpen = expanded[layerId];
          const hasControls = (spec?.controls || []).length > 0;
          const shown = shownIndicator(layerId);
          const title = shown?.label || layerLabel(layerId);
          const canChangeIndicator = !!spec?.indicatorControl
            && entries.some((e) => e.kind === "indicator" && e.layerId === layerId);
          return (
            <div key={layerId} className="view-composer-card">
              <div className="view-composer-card-head">
                <button
                  type="button"
                  className="view-composer-card-title"
                  onClick={() => setExpanded((prev) => ({ ...prev, [layerId]: !prev[layerId] }))}
                  disabled={!hasControls}
                  aria-expanded={isOpen}
                >
                  <span className={`view-composer-chevron${isOpen ? " open" : ""}`} aria-hidden="true" />
                  <span className="view-composer-card-titles">
                    <span className="view-composer-card-name">{title}</span>
                    {shown && (
                      <span className="view-composer-card-origin">
                        {shown.pending ? `${layerLabel(layerId)} · indicateur par défaut` : layerLabel(layerId)}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  className="view-composer-remove"
                  onClick={() => removeLayer(layerId)}
                  title="Retirer cette couche de la vue"
                  aria-label={`Retirer ${layerLabel(layerId)} de la vue`}
                >
                  ✕
                </button>
              </div>

              {isOpen && (
                <div className="view-composer-card-body">
                  {docs?.summary && <p className="view-composer-card-summary">{docs.summary}</p>}
                  {canChangeIndicator && (
                    // Remplace la liste déroulante de vingt-neuf entrées : le
                    // choix se fait dans l'explorateur, rangé par thème, le
                    // même que pour ajouter une couche.
                    <button
                      type="button"
                      className="view-composer-change"
                      onClick={() => setBrowserScope(layerId)}
                    >
                      Changer ce qui est affiché…
                    </button>
                  )}
                  {hasControls ? (
                    <LayerControlGroup
                      layerId={layerId}
                      settings={layerSettings[layerId]}
                      catalog={catalog}
                      onChange={(controlId, value) => setSetting(layerId, controlId, value)}
                      exclude={spec?.indicatorControl ? [spec.indicatorControl] : []}
                    />
                  ) : (
                    <p className="builder-empty-inline">Cette couche n'a aucun réglage.</p>
                  )}
                  {(docs?.caveats || []).map((caveat) => (
                    <p key={caveat} className="view-composer-caveat">{caveat}</p>
                  ))}
                  {docs?.source && (
                    <p className="view-composer-card-source">Source : {docs.source}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {suggestionsByTheme.length > 0 && (
        <div className="view-composer-available">
          <div className="builder-label-text">Ajouter une couche</div>
          {suggestionsByTheme.map((theme) => (
            <div key={theme.id} className="view-composer-availgroup">
              <span className="view-composer-availgroup-title" title={theme.description}>
                {theme.label}
              </span>
              <div className="view-composer-chips">
                {theme.entries.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className="view-composer-addchip"
                    onClick={() => addLayer(entry.layerId)}
                    title={entry.detail}
                  >
                    + {entry.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {browserScope && (
        <LayerBrowser
          entries={entries}
          themes={themes}
          activeLayers={activeLayers}
          layerSettings={layerSettings}
          restrictToLayer={browserScope === "all" ? null : browserScope}
          restrictLabel={browserScope === "all" ? null : layerLabel(browserScope)}
          onPick={(entry) => {
            applyResult(entry);
            // Restreint à une couche, on vient CHANGER une valeur : le choix
            // fait, il n'y a plus rien à y faire. Sur tout le catalogue, on
            // explore — la fenêtre reste ouverte pour en ajouter plusieurs.
            if (browserScope !== "all") setBrowserScope(null);
          }}
          onClose={() => setBrowserScope(null)}
        />
      )}
    </div>
  );
}
